import React from "react";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Modal from "@amzn/meridian/esm/components/modal";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import Icon from "@amzn/meridian/esm/components/icon";
import errorTokens from "@amzn/meridian-tokens/base/icon/error";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import { scaleIconClass } from "../../styles/icon.styles";
import { ALERT_MODAL_THEME_TOKENS } from "./alertModal.constants";
import { resolveVariant } from "./alertModal.utils";

// Re-export so call sites can import the component and its variant enum from one place.
export { ALERT_VARIANT } from "./alertModal.constants";

/**
 * AlertModal - the single error/alert popup for AiMS. Renders the teal-header Modal, the 40px
 * error icon, a title (one or more h100 lines), stacked b200 message lines, and a centered row
 * of action buttons.
 *
 * Pass a `variant` (see ALERT_VARIANT) plus its callbacks (onClose / onCancel / onStart /
 * onRestart) and params (context, errorType) to use a preset. Or pass `title` / `message` /
 * `actions` directly for a one-off. There are intentionally no per-variant wrapper components —
 * add a case to resolveVariant (AlertModal.utils.js) instead.
 *
 * @param {boolean} isOpen - controls modal visibility
 * @param {string} [variant] - one of the ALERT_VARIANT presets
 * @param {string|string[]} [title] - heading line(s), when not using a variant
 * @param {string|string[]} [message] - body line(s), when not using a variant
 * @param {Array<{label, onClick, type?, minWidth?}>} [actions] - buttons, when not using a variant
 * @returns {JSX.Element}
 */
const AlertModal = ({ isOpen, variant, title, message, actions, ...rest }) => {
  const resolved = variant ? resolveVariant(variant, rest) : { title, message, actions };
  const titles = Array.isArray(resolved.title) ? resolved.title : resolved.title != null ? [resolved.title] : [];
  const messages = Array.isArray(resolved.message) ? resolved.message : resolved.message != null ? [resolved.message] : [];
  const acts = resolved.actions || [];

  return (
    <Theme palette={aimsTheme} tokens={ALERT_MODAL_THEME_TOKENS}>
      <Modal
        open={isOpen}
        closeLabel={null}
        shouldCloseOnOverlayClick={true}
        fullscreenBreakpoint={0}
        title=" "
        bodySpacingInset="400"
        width="292px"
      >
        <Column spacing="400" spacingInset="400 none">
          <Row alignmentHorizontal="center" alignmentVertical="center" height="40px">
            <Icon className={scaleIconClass(1.67)} tokens={errorTokens} color="error" />
          </Row>
          <Column spacing="300" spacingInset="none 450">
            {titles.map((t, i) => (
              <Text key={`t${i}`} type="h100" alignment="start">
                {t}
              </Text>
            ))}
            {messages.map((m, i) => (
              <Text key={`m${i}`} type="b200" alignment="start">
                {m}
              </Text>
            ))}
          </Column>
          <Row alignmentHorizontal="center" spacing="400">
            {acts.map((a, i) => (
              <Button key={i} type={a.type || "primary"} size="small" minWidth={a.minWidth} onClick={a.onClick}>
                {a.label}
              </Button>
            ))}
          </Row>
        </Column>
      </Modal>
    </Theme>
  );
};

export default AlertModal;
